/*
  A note of what the app actually did, kept where it cannot be silently refused.

  **Written 7 September 2026, after six wrong diagnoses of one fault**, and
  rewritten within the hour after the first version was refused by the database
  and left nothing behind at all.

  **The first version wrote to the audit log and every line was thrown away.**
  That table only accepts a row that names its author, and the notes did not
  name one, so the database changed nothing, raised nothing, and answered fine.
  That is this project's oldest fault, walked into by the very thing built to
  diagnose a fault. It is written here rather than quietly corrected because it
  is the second time in one evening that a silent refusal cost a round.

  **So the notes are kept in the browser first**, where nothing can refuse them,
  under the key `onhand.trace`. **They are not shown on any screen** (corrected
  19 September 2026, the audit after the rebrand: this said they were shown on
  the sign-in screen, and nothing has drawn them there since the rebrand). To
  read them, open the browser's developer tools on the OnHand page and look at
  that key in Local Storage, or ask a session to. The database copy is still
  attempted, now naming its author, but nothing depends on it.
*/
const KEY = 'onhand.trace';
const MAX = 40;

// Kept so the callers need no change, and so this can start recording who
// again in one line if it is ever needed.
export function traceWho() {}

export function traceLines() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
}

export function traceClear() {
  try { localStorage.removeItem(KEY); } catch { /* nothing to do */ }
}

export function trace(step, detail) {
  const when = new Date();
  const stamp = String(when.getHours()).padStart(2, '0') + ':'
              + String(when.getMinutes()).padStart(2, '0') + ':'
              + String(when.getSeconds()).padStart(2, '0') + '.'
              + String(when.getMilliseconds()).padStart(3, '0');
  /*
    **A note can never stop the thing it is noting**, 18 September 2026. This
    line used to throw on anything that cannot be written out (a click event
    handed to sign-out, which refers back to itself), and it sat outside any
    guard, so writing the note stopped Sign out dead on four screens.
  */
  let said;
  try { said = JSON.stringify(detail || {}); } catch { said = '(could not be written out)'; }
  const line = stamp + '  ' + step + '  ' + said;
  try {
    const all = traceLines();
    all.push(line);
    localStorage.setItem(KEY, JSON.stringify(all.slice(-MAX)));
  } catch { /* a browser that refuses storage still gets the copy below */ }
  /*
    **It no longer writes into the audit log.** It did while the fault was being
    hunted, and it left 31 rows in the record of what people actually did, which
    is not the place for a diagnostic. The notes live in the browser, which is
    where they were read from in the end anyway.
  */
}

/*
  **Anything that throws, anywhere, goes in the notes too.** Added 7 September
  2026. OnHand has no error boundary, so a component that throws while drawing
  takes the whole app down with it and React says so only in a console nobody
  has open. That is one of the few explanations left for a screen that appears
  and vanishes, and it was not being recorded at all.
*/
if (typeof window !== 'undefined' && !window.__onhandTraceHooked) {
  window.__onhandTraceHooked = true;
  window.addEventListener('error', e => {
    trace('uncaught', { message: String(e?.message || ''), at: String(e?.filename || '') + ':' + (e?.lineno || '') });
  });
  window.addEventListener('unhandledrejection', e => {
    trace('unhandledRejection', { reason: String(e?.reason?.message || e?.reason || '').slice(0, 300) });
  });
  // Leaving the page at all is worth a line: it separates "the app changed
  // screens" from "the browser went somewhere", which is the difference
  // between a flash and a reload and has never been established.
  window.addEventListener('beforeunload', () => trace('pageLeaving', {}));
  trace('pageLoaded', { url: String(window.location.href).slice(0, 120) });
}
